/**
 * Chat Input - Text input + send button for admin chat panel.
 *
 * Matches the sci-fi HUD theme with neon accent on active send button.
 */

import React, { useState, KeyboardEvent } from 'react';
import { Box, TextField, IconButton, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useMissionTheme } from '../theme';

interface ChatInputProps {
  onSend: (text: string) => Promise<void>;
  disabled?: boolean;
}

export default function ChatInput({ onSend, disabled }: ChatInputProps) {
  const { mc } = useMissionTheme();
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    if (!text.trim() || isSending || disabled) return;
    setIsSending(true);
    try {
      await onSend(text.trim());
      setText('');
    } catch (err) {
      console.error('[ChatInput] Send failed:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        pt: 1.5,
        borderTop: `1px solid ${mc.border}`,
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={3}
        size="small"
        placeholder="Type a message..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: mc.surface,
            color: mc.textPrimary,
            fontSize: '0.85rem',
            borderRadius: 2,
            '& fieldset': { borderColor: mc.border },
            '&:hover fieldset': { borderColor: mc.secondary },
            '&.Mui-focused fieldset': { borderColor: mc.secondary },
          },
          '& .MuiInputBase-input::placeholder': {
            color: mc.textMuted,
            opacity: 1,
          },
        }}
      />
      <IconButton
        onClick={handleSend}
        disabled={!text.trim() || isSending || disabled}
        sx={{
          color: text.trim() ? mc.secondary : mc.textMuted,
          '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)' },
        }}
      >
        {isSending ? <CircularProgress size={20} sx={{ color: mc.secondary }} /> : <SendIcon />}
      </IconButton>
    </Box>
  );
}
